import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import * as React from 'react'

import {
  ExportIcon,
  FilterIcon,
  SuspendUserIcon,
  BlockUserIcon,
  UsersIcon,
} from '@/components/Icons'
import { Button } from '@/components/ui/button'
import { TableSkeleton } from '@/components/TableSkeletons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import FilterDrawer, { type FilterValues } from '@/components/FilterDrawer'
import FilterBar from '@/components/FilterBar'
import { showErrorToast, showSuccessToast } from '@/components/ui/toast'
import { UserActionDialog } from '@/components/UserActionDialog'
import { AssignRoleDrawer } from '@/components/AssignRoleDialog'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  assignRoleBulkUser,
  blockBulkUser,
  getRoles,
  suspendBulkUser,
} from '@/api/userApi'
import { transformRolesToOptions } from '@/utils/helper'
import { eventStatus } from '@/constants/event'
import { USER_ACTIONS, USER_STATUSES } from '@/constants'
import { USER_QUERY_KEYS } from '@/api/queryKeys'
import type { AxiosError } from 'axios'
import { cn } from '@/lib/utils'
import { attendeeStatus } from '@/constants/attendee'
import { EventSelect, type EventSelectValue } from './event/EventSelect'

export type DataTableProps<TData, TValue> = {
  title?: string
  subtitle?: string
  data: TData[]
  sorting: SortingState
  setSorting: (updater: React.SetStateAction<SortingState>) => void
  pagination: {
    pageIndex: number
    pageSize: number
  }
  setPagination: (
    updater: React.SetStateAction<{ pageIndex: number; pageSize: number }>
  ) => void
  columns: ColumnDef<TData, TValue>[]
  onFilterClick?: () => void
  onExportClick?: () => void
  onSecondExportClick?: () => void
  filterButtonText?: string
  exportButtonText?: string
  secondExportButtonText?: string
  showFilter?: boolean
  showExport?: boolean
  isExportLoading?: boolean
  showSecondExport?: boolean
  isSecondExportLoading?: boolean
  emptyState?: {
    image: string
    title: string
    subtitle: string
  }
  isLoading?: boolean
  error?: string | null
  totalItems?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  onFiltersChange?: (filters: FilterValues) => void
  currentPage?: number
  pageSize?: number
  totalPages?: number
  roleOptions?: { id: string; name: string; title: string }[]
  userFilter?: boolean
  eventFilter?: boolean
  categories?: { id: string; name: string }[]
  showEventSelect?: boolean
  attendeeFilter?: boolean
  parentClassName?: string
  tableFilterSkeleton?: boolean
  isDisableFilterFutureDates?: boolean
}

export type DataTableHandle = {
  getActiveFilters: () => FilterValues
}

interface UserItem {
  id: string
  firstName: string
  lastName: string
  avatar?: string
}

export const DataTable = React.forwardRef(
  <TData, TValue>(
    {
      title,
      subtitle,
      data,
      columns,
      sorting,
      setSorting,
      pagination,
      setPagination,
      onFilterClick,
      onExportClick,
      onSecondExportClick,
      filterButtonText = 'Filter',
      exportButtonText = 'Export CSV',
      secondExportButtonText = 'Export CSV',
      showFilter = true,
      showExport = true,
      isExportLoading = false,
      showSecondExport = false,
      isSecondExportLoading = false,
      emptyState,
      isLoading = false,
      error = null,
      totalItems = 0,
      onPageChange,
      onPageSizeChange,
      onFiltersChange,
      currentPage = 1,
      pageSize = 10,
      totalPages = 1,
      roleOptions = [],
      userFilter = false,
      eventFilter = false,
      categories = [],
      showEventSelect = false,
      attendeeFilter = false,
      parentClassName = '',
      tableFilterSkeleton = true,
      isDisableFilterFutureDates = false,
    }: DataTableProps<TData, TValue>,
    ref: React.Ref<DataTableHandle>
  ) => {
    const [rowSelection, setRowSelection] = React.useState({})
    const [columnVisibility, setColumnVisibility] =
      React.useState<VisibilityState>({})
    const [columnFilters, setColumnFilters] =
      React.useState<ColumnFiltersState>([])
    const [selectedEvent, setSelectedEvent] =
      React.useState<EventSelectValue | null>(null)

    const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false)
    const [activeFilters, setActiveFilters] = React.useState<FilterValues>({
      userRole: [],
      subscriptionType: '',
      eventAttendance: '',
      registrationEndDate: '',
      registrationStartDate: '',
      status: [],
      statusEvent: '',
      category: [],
      purchaseDate: '',
      eventId: '',
    })
    const queryClient = useQueryClient()

    React.useImperativeHandle(
      ref,
      () => ({
        getActiveFilters: () => activeFilters,
      }),
      [activeFilters]
    )

    // Bulk action states
    const [bulkActionDialogOpen, setBulkActionDialogOpen] =
      React.useState(false)
    const [bulkActionType, setBulkActionType] = React.useState<
      typeof USER_ACTIONS.BLOCK | typeof USER_ACTIONS.SUSPEND
    >(USER_ACTIONS.SUSPEND)
    const [assignRoleDialogOpen, setAssignRoleDialogOpen] =
      React.useState(false)

    // Add roles query if not passed via props
    const { data: rolesData } = useQuery({
      queryKey: ['roles'],
      queryFn: getRoles,
      enabled: !roleOptions.length, // Only fetch if not provided
    })

    const typedData = data as UserItem[]

    // Compute final roleOptions
    const finalRoleOptions =
      roleOptions.length > 0 ? roleOptions : transformRolesToOptions(rolesData)

    React.useEffect(() => {
      setPagination({
        pageIndex: currentPage - 1,
        pageSize: pageSize,
      })
    }, [currentPage, pageSize])

    const table = useReactTable({
      data,
      columns,
      columnResizeMode: 'onChange',
      state: {
        sorting,
        columnVisibility,
        rowSelection,
        columnFilters,
        pagination,
      },
      getRowId: (row: TData, index: number) => {
        const rowWithId = row as TData & { id?: string | number }
        return rowWithId.id?.toString() || index.toString()
      },
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      manualPagination: true,
      pageCount: totalPages,
    })

    const handleEventSelectChange = (
      event: EventSelectValue | EventSelectValue[] | null
    ) => {
      let newEventId = ''
      let selectedEventData: EventSelectValue | null = null
      if (event) {
        if (Array.isArray(event)) {
          newEventId = event[0]?.id || ''
          selectedEventData = event[0] || null
        } else {
          newEventId = event.id
          selectedEventData = event
        }
      }

      const newFilters = {
        ...activeFilters,
        eventId: newEventId,
      }
      setSelectedEvent(selectedEventData)
      setActiveFilters(newFilters)
      onFiltersChange?.(newFilters)
    }

    const handleFilterClick = () => {
      setFilterDrawerOpen(true)
      onFilterClick?.()
    }

    // Bulk action handlers
    const selectedRows = table.getFilteredSelectedRowModel().rows
    const hasSelectedRows = selectedRows.length > 0

    const hasBannedUser = selectedRows.some(row => {
      const userData = row.original as { status?: string }
      return userData.status?.toLowerCase() === USER_STATUSES.banned
    })

    const { mutate: suspendBulkUserMutation, isPending } = useMutation({
      mutationFn: suspendBulkUser,
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: [USER_QUERY_KEYS.all],
          exact: false,
        })

        showSuccessToast(data.message)
        setBulkActionDialogOpen(false)
      },
      onError: (err: unknown) => {
        console.error('Suspend users failed:', err)
        let description = 'Please try again or contact support.'
        if (err instanceof Error) {
          const axiosErr = err as AxiosError<{ message: string }>
          description = axiosErr.response?.data?.message ?? axiosErr.message
        }

        showErrorToast('Failed to suspend users', description)
      },
    })

    const { mutate: blockBulkUserMutation } = useMutation({
      mutationFn: blockBulkUser,
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: [USER_QUERY_KEYS.all],
          exact: false,
        })

        showSuccessToast(data.message)
        setBulkActionDialogOpen(false)
      },
      onError: (err: unknown) => {
        console.error('Block users failed:', err)
        let description = 'Please try again or contact support.'
        if (err instanceof Error) {
          const axiosErr = err as AxiosError<{ message: string }>
          description = axiosErr.response?.data?.message ?? axiosErr.message
        }

        showErrorToast('Failed to suspend users', description)
      },
    })

    const handleBulkAssignRole = () => {
      setAssignRoleDialogOpen(true)
    }

    const handleBulkSuspend = () => {
      setBulkActionType(USER_ACTIONS.SUSPEND)
      setBulkActionDialogOpen(true)
    }

    const handleBulkBlock = () => {
      setBulkActionType(USER_ACTIONS.BLOCK)
      setBulkActionDialogOpen(true)
    }

    const handleBulkActionConfirm = async (formData: {
      duration: number
      reason: string
      notes?: string
    }) => {
      // Reset selection after action
      table.resetRowSelection()
      const payload = {
        userIds: selectedRows.map(id => id.id),
        ...formData,
      }

      if (bulkActionType === USER_ACTIONS.SUSPEND) {
        suspendBulkUserMutation(payload)
      } else if (bulkActionType === USER_ACTIONS.BLOCK) {
        blockBulkUserMutation({
          userIDs: selectedRows.map(id => id.id),
          reason: formData.reason,
          notes: formData.notes,
        })
      }
    }

    const { mutate: assignRoleBulkUserMutation } = useMutation({
      mutationFn: assignRoleBulkUser,
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: [USER_QUERY_KEYS.all],
          exact: false,
        })

        showSuccessToast(data.message)
        setAssignRoleDialogOpen(false)
        table.resetRowSelection()
      },
      onError: error => {
        console.error('Assign roles failed:', error)
        showErrorToast('Failed to assign roles', error.message)
      },
    })

    const selectedRowIds = table
      .getFilteredSelectedRowModel()
      .rows.map(row => row.id)

    const handleAssignRoleConfirm = (roleId: string) => {
      const userIds = selectedRowIds

      assignRoleBulkUserMutation({ userIds, roleId })
    }

    const selectedUsersData = typedData
      .filter(item => selectedRowIds.includes(item.id))
      .map(item => ({
        id: item.id,
        name: `${item.firstName} ${item.lastName}`,
        avatar: item.avatar,
      }))

    const handleApplyFilters = (filters: FilterValues) => {
      setActiveFilters(filters)
      onFiltersChange?.(filters)
      showSuccessToast('Filters applied successfully.')
    }

    const handleRemoveFilter = (key: keyof FilterValues, id?: string) => {
      if (Array.isArray(activeFilters[key]) && id) {
        const newFilters = {
          ...activeFilters,
          [key]: activeFilters[key].filter(itemId => itemId !== id),
        }
        setActiveFilters(newFilters)
        onFiltersChange?.(newFilters)
      } else {
        const newFilters = {
          ...activeFilters,
          [key]: key === 'userRole' ? [] : '',
        }
        if (key === 'eventId') {
          setSelectedEvent(null)
        }
        setActiveFilters(newFilters)
        onFiltersChange?.(newFilters)
      }
    }

    const handleResetFilters = () => {
      const emptyFilters: FilterValues = {
        userRole: [],
        subscriptionType: '',
        eventAttendance: '',
        registrationEndDate: '',
        registrationStartDate: '',
        // registrationDate: '', --- IGNORE ---
        status: [],
        statusEvent: '',
        category: [],
        purchaseDate: '',
        eventId: '',
      }
      setActiveFilters(emptyFilters)
      setSelectedEvent(null)
      onFiltersChange?.(emptyFilters)
    }

    const hasActiveFilters =
      activeFilters.userRole.length > 0 ||
      Object.entries(activeFilters).some(
        ([key, value]) => key !== 'userRole' && value !== ''
      )
    const buildPageNumbers = () => {
      const pages: (number | string)[] = []
      const maxVisible = 5

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)

        if (currentPage > 3) {
          pages.push('...')
        }

        const start = Math.max(2, currentPage - 1)
        const end = Math.min(totalPages - 1, currentPage + 1)

        for (let i = start; i <= end; i++) {
          if (i !== 1 && i !== totalPages) {
            pages.push(i)
          }
        }

        if (currentPage < totalPages - 2) {
          pages.push('...')
        }

        if (totalPages > 1) {
          pages.push(totalPages)
        }
      }

      return pages
    }

    const pageNumbers = buildPageNumbers()

    if (isLoading) {
      return (
        <TableSkeleton
          rows={10}
          columns={columns.length}
          filterSkeleton={tableFilterSkeleton}
        />
      )
    }

    return (
      <>
        <div className="w-full flex flex-col h-full">
          {/* Table Header Section - Sticky */}
          {title && (
            <header
              className="flex items-center justify-between px-4 lg:px-6 py-4 bg-transparent sticky top-0 z-10 flex-wrap gap-2"
              aria-label="Table actions"
            >
              <div>
                <h2 className="text-xl font-bold text-ss-neutral-950">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-sm text-ss-neutral-600">{subtitle}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Bulk Actions - shown when rows are selected */}
                {hasSelectedRows && (
                  <div
                    className="flex items-center gap-3"
                    role="group"
                    aria-label="Bulk actions"
                  >
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleBulkAssignRole}
                              disabled={hasBannedUser}
                              aria-label="Assign role to selected users"
                            >
                              <UsersIcon
                                className="h-4 w-4"
                                strokeWidth={2.5}
                                aria-hidden="true"
                              />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {hasBannedUser
                            ? 'Cannot assign role to banned users'
                            : 'Assign Role'}
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleBulkSuspend}
                              disabled={hasBannedUser}
                              aria-label="Suspend selected users"
                            >
                              <SuspendUserIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {hasBannedUser
                            ? 'One or more users are already banned'
                            : 'Suspend User'}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleBulkBlock}
                              disabled={hasBannedUser}
                              aria-label="Block selected users"
                            >
                              <BlockUserIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {hasBannedUser
                            ? 'One or more users are already banned'
                            : 'Block User'}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                )}

                {/* Regular Actions - shown when no rows are selected */}
                <div className="flex items-center gap-3 flex-wrap">
                  {showEventSelect && (
                    <EventSelect
                      variant="compact"
                      value={selectedEvent}
                      onValueChange={handleEventSelectChange}
                      placeholder="All Events"
                      enableSearch
                    />
                  )}
                  {showFilter && (
                    <Button
                      variant="outline"
                      onClick={handleFilterClick}
                      aria-label="Open filter drawer"
                    >
                      <FilterIcon className="h-4 w-4" aria-hidden="true" />
                      {filterButtonText}
                    </Button>
                  )}
                  {showExport && (
                    <Button
                      variant="default"
                      onClick={onExportClick}
                      aria-label="Export table as CSV"
                      disabled={isExportLoading}
                    >
                      <ExportIcon className="h-4 w-4" aria-hidden="true" />
                      {exportButtonText}
                    </Button>
                  )}
                  {showSecondExport && (
                    <Button
                      variant="default"
                      onClick={onSecondExportClick}
                      aria-label="Export table as CSV"
                      disabled={isSecondExportLoading}
                    >
                      <ExportIcon className="h-4 w-4" aria-hidden="true" />
                      {secondExportButtonText}
                    </Button>
                  )}
                </div>
              </div>
            </header>
          )}

          {/* Filter Bar - Sticky */}
          {hasActiveFilters && (
            <div
              className="sticky top-[73px] z-10 bg-transparent"
              aria-live="polite"
              aria-atomic="true"
            >
              <FilterBar
                filters={activeFilters}
                onRemoveFilter={handleRemoveFilter}
                onResetFilters={handleResetFilters}
                roleOptions={finalRoleOptions}
                categoryOptions={categories}
                selectedEvent={selectedEvent}
              />
            </div>
          )}
          {/* Data Table - Scrollable Content */}
          <div
            className={cn(
              'flex-1 mx-4 lg:mx-6 rounded-xl border border-ss-neutral-50 bg-ss-neutral-white overflow-x-auto flex flex-col mb-4 lg-mb-6',
              parentClassName
            )}
          >
            <div className="flex-1 overflow-auto">
              <Table role="table" aria-label={title} className="min-w-full">
                <TableHeader className="bg-ss-neutral-25 sticky top-0 z-10">
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow
                      key={headerGroup.id}
                      className="border-b border-ss-neutral-50 hover:bg-ss-neutral-25"
                      role="row"
                    >
                      {headerGroup.headers.map(header => {
                        // Add aria-sort and aria-label for sortable columns
                        const isSortable = header.column.getCanSort?.() ?? false
                        const sortDirection = header.column.getIsSorted?.()
                        let ariaSort: 'none' | 'ascending' | 'descending' =
                          'none'
                        if (sortDirection === 'asc') ariaSort = 'ascending'
                        if (sortDirection === 'desc') ariaSort = 'descending'
                        return (
                          <TableHead
                            key={header.id}
                            colSpan={header.colSpan}
                            style={{
                              ...(header.getSize() && {
                                width: `${header.getSize()}px`,
                              }),
                              ...(header.column.columnDef.minSize && {
                                minWidth: `${header.column.columnDef.minSize}px`,
                              }),
                              ...(header.column.columnDef.maxSize && {
                                maxWidth: `${header.column.columnDef.maxSize}px`,
                              }),
                            }}
                            className={cn(
                              'h-12 px-4 text-left font-medium text-ss-neutral-950 bg-ss-neutral-25',
                              header.column.id === 'actions' &&
                                'sticky right-0 z-20 box-shadow-action'
                            )}
                            role="columnheader"
                            aria-sort={isSortable ? ariaSort : undefined}
                            aria-label={
                              isSortable
                                ? `Sort by ${String(header.column.id)}`
                                : undefined
                            }
                            tabIndex={isSortable ? 0 : undefined}
                            onKeyDown={
                              isSortable
                                ? e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      header.column.toggleSorting?.()
                                    }
                                  }
                                : undefined
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        )
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-96 p-0">
                        <div className="flex items-center justify-center flex-col gap-2 py-8 w-full h-full">
                          <p className="text-ss-neutral-950 text-base font-bold">
                            Loading...
                          </p>
                          <p className="text-ss-neutral-600 text-sm font-normal">
                            Please wait while we fetch your data
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-96 p-0">
                        <div className="flex items-center justify-center flex-col gap-2 py-8 w-full h-full">
                          <p className="text-ss-neutral-950 text-base font-bold">
                            Error loading data
                          </p>
                          <p className="text-ss-neutral-600 text-sm font-normal">
                            {error}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map(row => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        className="group border-b border-ss-neutral-50 hover:bg-ss-action-gray-50"
                      >
                        {row.getVisibleCells().map(cell => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              'px-4 py-4',
                              cell.column.id === 'actions' &&
                                'sticky right-0 z-20 bg-ss-neutral-white group-hover:bg-ss-action-gray-50 box-shadow-action'
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-96 p-0">
                        {emptyState ? (
                          <div className="flex items-center justify-center flex-col gap-2 py-8 w-full h-full">
                            <img
                              src={emptyState.image || '/placeholder.svg'}
                              alt="empty state"
                              className="mb-2"
                            />
                            <p className="text-ss-neutral-950 text-base font-bold">
                              {emptyState.title}
                            </p>
                            <p className="text-ss-neutral-600 text-sm text-center font-normal text-wrap max-w-md">
                              {emptyState.subtitle}
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center flex-col gap-2 py-8 w-full h-full">
                            <p className="text-ss-neutral-950 text-base font-bold">
                              No results found
                            </p>
                            <p className="text-ss-neutral-600 text-sm font-normal">
                              Try adjusting your search or filters
                            </p>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Section - Sticky Footer */}
            <div className="flex items-center justify-between p-4 border-t border-ss-neutral-50 bg-ss-neutral-white sticky bottom-0 z-10 flex-wrap gap-2">
              <div className="text-sm font-medium text-ss-neutral-950">
                Total {totalItems} items
              </div>

              <div className="flex items-center gap-2">
                {/* Pagination Numbers */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 !h-8 p-0 hover:bg-ss-action-gray-50"
                    onClick={() => onPageChange?.(1)}
                    disabled={currentPage === 1}
                  >
                    <IconChevronLeft className="h-4 w-4" />
                  </Button>

                  {pageNumbers.map((page, index) => (
                    <React.Fragment key={`page-${page}-${index}`}>
                      {page === '...' ? (
                        <span className="px-2 text-ss-neutral-600">...</span>
                      ) : (
                        <Button
                          variant={currentPage === page ? 'default' : 'ghost'}
                          size="sm"
                          className={`w-8 h-8 p-0 ${
                            currentPage === page
                              ? 'bg-transparent border border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-25'
                              : ' hover:bg-ss-action-gray-50'
                          }`}
                          onClick={() => onPageChange?.(page as number)}
                        >
                          {page}
                        </Button>
                      )}
                    </React.Fragment>
                  ))}

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 !h-8 p-0 hover:bg-ss-action-gray-50"
                    onClick={() => onPageChange?.(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <IconChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 border-ss-neutral-200 gap-1 text-ss-neutral-950 hover:bg-ss-action-gray-50 bg-transparent"
                  >
                    {pageSize} / page
                    <IconChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {[5, 10, 20, 30, 40, 50].map(size => (
                    <DropdownMenuItem
                      key={size}
                      onClick={() => onPageSizeChange?.(size)}
                    >
                      {size} / page
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Filter Drawer */}

        {userFilter && (
          <FilterDrawer
            registerLabel
            open={filterDrawerOpen}
            onOpenChange={setFilterDrawerOpen}
            onApplyFilters={handleApplyFilters}
            fields={[
              'userRole',
              'eventAttendance',
              'registrationStartDate',
              'registrationEndDate',
              'status',
            ]}
            fieldOptions={{
              userRole: roleOptions,
              eventAttendance: [
                '0-5 events',
                '6-10 events',
                '11-15 events',
                '16+ events',
              ],
              status: ['Active', 'Suspended', 'Banned'],
            }}
            appliedFilter={activeFilters}
            isDisableFutureDates={isDisableFilterFutureDates}
          />
        )}

        {eventFilter && (
          <FilterDrawer
            open={filterDrawerOpen}
            onOpenChange={setFilterDrawerOpen}
            onApplyFilters={handleApplyFilters}
            fields={[
              'registrationStartDate',
              'registrationEndDate',
              'category',
              'statusEvent',
            ]}
            fieldOptions={{
              category: categories,
              eventAttendance: [
                '0-50 attendees',
                '51-100 attendees',
                '101-200 attendees',
                '200+ attendees',
              ],
              status: Object.values(eventStatus),
            }}
            appliedFilter={activeFilters}
          />
        )}

        {attendeeFilter && (
          <FilterDrawer
            open={filterDrawerOpen}
            onOpenChange={setFilterDrawerOpen}
            onApplyFilters={handleApplyFilters}
            fields={['purchaseDate', 'statusAttendee']}
            fieldOptions={{
              category: categories,
              eventAttendance: [
                '0-50 attendees',
                '51-100 attendees',
                '101-200 attendees',
                '200+ attendees',
              ],
              status: Object.values(attendeeStatus),
            }}
            appliedFilter={activeFilters}
          />
        )}

        {/* Bulk Action Dialogs */}
        <UserActionDialog
          open={bulkActionDialogOpen}
          onOpenChange={setBulkActionDialogOpen}
          type={bulkActionType}
          onConfirm={handleBulkActionConfirm}
          isLoading={isPending}
          title={
            bulkActionType === USER_ACTIONS.SUSPEND
              ? 'Are you sure you want to suspend these users?'
              : 'Are you sure you want to block these users?'
          }
          subtext={
            bulkActionType === USER_ACTIONS.SUSPEND
              ? 'Suspending will temporarily restrict access to the platform. The users can be reactivated later.'
              : 'Blocking will permanently prevent access to the platform. The users will not be able to log in again unless unblocked by an admin.'
          }
          confirmButtonText={
            bulkActionType === USER_ACTIONS.SUSPEND ? 'Suspend' : 'Block'
          }
        />

        <AssignRoleDrawer
          open={assignRoleDialogOpen}
          onOpenChange={setAssignRoleDialogOpen}
          onConfirm={handleAssignRoleConfirm}
          selectedCount={selectedRows.length}
          selectedUsers={selectedUsersData}
        />
      </>
    )
  }
) as <TData, TValue>(
  props: DataTableProps<TData, TValue> & React.RefAttributes<DataTableHandle>
) => React.ReactElement
